import React, { useMemo, useState } from 'react';
import { Alert, StyleSheet, Switch, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  SearchableEntitySelector,
  type SelectorOption,
} from '../../components/SearchableEntitySelector';
import { SearchableMultiSelect } from '../../components/SearchableMultiSelect';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { getDepartments } from '../../services/departmentService';
import { getDesignations } from '../../services/designationService';
import { createEmployee, getAssignableRoles } from '../../services/employeeService';
import { ensureAzurePerson } from '../../services/employeeFaceRegistrationService';
import { getLocations } from '../../services/locationService';
import { activeConfigurationFilters } from '../../utils/configurationFilters';
import { normalizeApiError } from '../../api/apiError';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';
import { useAuth } from '../../context/AuthContext';
import { ROLE_HR_ADMIN } from '../../utils/approvalRoles';
import { filterRolesForEmployeeCreate } from '../../utils/employeeCreateRoles';
import type { EmployeeCreateResult } from '../../types/employee';

type Props = NativeStackScreenProps<RootStackParamList, 'EmployeeCreate'>;

/** What the server assigned automatically (default leave policies). */
function leavePolicyNote(created: EmployeeCreateResult): string {
  if (created.leavePolicies.length > 0) {
    return `Leave policies assigned: ${created.leavePolicies
      .map(policy => policy.policyName)
      .join(', ')}.`;
  }
  return created.leavePolicyWarning ?? 'No leave policy was assigned.';
}

export function EmployeeCreateScreen({ navigation }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  // The server assigns the default WFH policy; only HR Admin may enable WFH.
  const canSetWfh = auth.hasRole(ROLE_HR_ADMIN);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [department, setDepartment] = useState<SelectorOption | null>(null);
  const [designation, setDesignation] = useState<SelectorOption | null>(null);
  const [location, setLocation] = useState<SelectorOption | null>(null);
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [officialEmail, setOfficialEmail] = useState('');
  const [username, setUsername] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('TempPass@123');
  const [roles, setRoles] = useState<SelectorOption[]>([]);
  const [faceAttendanceEnabled, setFaceAttendanceEnabled] = useState(true);
  const [wfhEligible, setWfhEligible] = useState(false);

  const themed = useMemo(
    () => ({
      error: { ...typography.body, color: palette.error },
      label: { ...typography.caption, color: palette.textMuted },
      hint: { ...typography.caption, color: palette.textMuted },
    }),
    [palette],
  );

  async function loadDepartments(search: string): Promise<SelectorOption[]> {
    const result = await getDepartments({ page: 1, pageSize: 20, filters: activeConfigurationFilters({ search }) });
    return result.items.map(item => ({
      id: item.departmentId,
      label: item.departmentName,
      subtitle: item.departmentCode,
    }));
  }

  async function loadDesignations(search: string): Promise<SelectorOption[]> {
    const result = await getDesignations({ page: 1, pageSize: 20, filters: activeConfigurationFilters({ search }) });
    return result.items.map(item => ({
      id: item.designationId,
      label: item.designationName,
      subtitle: item.designationCode,
    }));
  }

  async function loadLocations(search: string): Promise<SelectorOption[]> {
    const result = await getLocations({ page: 1, pageSize: 20, filters: activeConfigurationFilters({ search }) });
    return result.items.map(item => ({
      id: item.locationId,
      label: item.locationName,
      subtitle: item.locationCode,
    }));
  }

  function loadRoles(search: string): Promise<SelectorOption[]> | SelectorOption[] {
    // Create Employee omits Office Kiosk / Team Leader (by role_code).
    const pending = getAssignableRoles(search, { purpose: 'create' });
    const mapItems = (items: Awaited<typeof pending>): SelectorOption[] =>
      filterRolesForEmployeeCreate(items).map(role => ({
        id: role.roleId,
        label: role.roleName,
        subtitle: role.description,
      }));
    if (typeof pending.then === 'function') return pending.then(mapItems);
    return mapItems(pending);
  }

  async function submit() {
    if (busy) return;
    setError('');
    if (!employeeNumber.trim() || !firstName.trim() || !officialEmail.trim() || !username.trim()) {
      setError('Employee number, first name, email, and username are required.');
      return;
    }
    if (roles.length === 0) {
      setError('Select at least one role.');
      return;
    }

    setBusy(true);
    try {
      const created = await runOperation('add', () => createEmployee({
        departmentId: department?.id ?? null,
        designationId: designation?.id ?? null,
        primaryLocationId: location?.id ?? null,
        reportingManagerId: null,
        employeeNumber: employeeNumber.trim(),
        firstName: firstName.trim(),
        middleName: null,
        lastName: lastName.trim() || null,
        officialEmail: officialEmail.trim(),
        personalEmail: null,
        mobileNumber: null,
        employmentType: 'FULL_TIME',
        joiningDate: new Date().toISOString().slice(0, 10),
        employeeStatusId: 1,
        username: username.trim(),
        temporaryPassword,
        userStatusId: 1,
        roleIds: roles.map(role => role.id),
        attendanceRequired: true,
        faceAttendanceEnabled,
        wfhEligible: canSetWfh && wfhEligible,
      }));

      let azurePersonReady = true;
      try {
        await ensureAzurePerson(created.employeeId);
      } catch {
        azurePersonReady = false;
      }

      Alert.alert(
        'Employee created',
        `${
          azurePersonReady
            ? `${created.displayName || created.employeeNumber || 'Employee'} is ready. Register their face next.`
            : `${created.displayName || created.employeeNumber || 'Employee'} was created, but Azure person setup failed. You can retry from face registration.`
        }\n\n${leavePolicyNote(created)}`,
        [
          {
            text: 'Register face',
            onPress: () =>
              navigation.replace(routes.employeeFaceRegistration, {
                employeeId: created.employeeId,
              }),
          },
          {
            text: 'View employee',
            onPress: () =>
              navigation.replace(routes.employeeDetails, {
                employeeId: created.employeeId,
              }),
          },
        ],
      );
    } catch (err) {
      const apiError = normalizeApiError(err);
      setError(apiError.message || 'Unable to create employee.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.EMPLOYEE_CREATE}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen webLayout="narrow" scroll keyboardSafe>
        <ScreenHeader
          title="Create employee"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
        <AppCard style={styles.card}>
          <SearchableEntitySelector
            label="Department (optional)"
            selectedValue={department?.id ?? null}
            selectedLabel={department?.label ?? null}
            loadOptions={loadDepartments}
            onSelect={setDepartment}
            onClear={() => setDepartment(null)}
          />
          <SearchableEntitySelector
            label="Designation (optional)"
            selectedValue={designation?.id ?? null}
            selectedLabel={designation?.label ?? null}
            loadOptions={loadDesignations}
            onSelect={setDesignation}
            onClear={() => setDesignation(null)}
          />
          <SearchableEntitySelector
            label="Primary location (optional)"
            selectedValue={location?.id ?? null}
            selectedLabel={location?.label ?? null}
            loadOptions={loadLocations}
            onSelect={setLocation}
            onClear={() => setLocation(null)}
          />
          <AppInput label="Employee number" value={employeeNumber} onChangeText={setEmployeeNumber} />
          <AppInput label="First name" value={firstName} onChangeText={setFirstName} />
          <AppInput label="Last name" value={lastName} onChangeText={setLastName} />
          <AppInput
            label="Official email"
            value={officialEmail}
            onChangeText={setOfficialEmail}
            autoCapitalize="none"
            keyboardType="email-address"
          />
          <AppInput
            label="Username"
            value={username}
            onChangeText={setUsername}
            autoCapitalize="none"
          />
          <AppInput
            label="Temporary password"
            value={temporaryPassword}
            onChangeText={setTemporaryPassword}
            secureTextEntry
          />
          <SearchableMultiSelect
            label="Roles"
            selected={roles}
            loadOptions={loadRoles}
            onChange={setRoles}
          />
          <Text style={themed.hint}>
            Select one or more roles. The first role you choose is the default
            role.
          </Text>
          <View style={styles.switchRow}>
            <Text style={themed.label}>Face attendance enabled</Text>
            <Switch
              value={faceAttendanceEnabled}
              onValueChange={setFaceAttendanceEnabled}
            />
          </View>
          {canSetWfh ? (
            <>
              <View style={styles.switchRow}>
                <Text style={themed.label}>WFH eligible</Text>
                <Switch
                  accessibilityLabel="WFH eligible"
                  value={wfhEligible}
                  onValueChange={setWfhEligible}
                />
              </View>
              <Text style={themed.hint}>
                The organization's default leave policies are assigned
                automatically
                {wfhEligible ? ', and so is the default WFH policy' : ''}. You
                can change them later from the employee's edit screen.
              </Text>
            </>
          ) : null}
          {error ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {error}
            </Text>
          ) : null}
          <AppButton
            title={busy ? 'Creating…' : 'Create employee'}
            onPress={submit}
            disabled={busy}
          />
          <AppButton
            title="Cancel"
            variant="secondary"
            onPress={() => navigation.goBack()}
            disabled={busy}
          />
        </AppCard>
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  card: { gap: spacing.md, marginTop: spacing.lg },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
});
